<script lang="ts">
    import { createDropdownMenu, createPopover, melt } from '@melt-ui/svelte';
    import { fly } from 'svelte/transition';
    import { cx } from '$lib/utils/cx.js';
    import { quadOut, quartInOut } from 'svelte/easing';
    import type { StoreElement } from '$lib/utils/types.js';
    import ButtonNavbar from '$components/Navbar/ButtonNavbar.svelte';
    import type { FloatingConfig } from '@melt-ui/svelte/internal/actions';

    function transitionIn(node: Element) {
        return fly(node, {
            delay: 0,
            duration: 300,
            easing: quadOut,
            y: -20,
            opacity: 0.5,
        });
    }

    function transitionOut(node: Element) {
        return fly(node, {
            delay: 0,
            duration: 300,
            easing: quartInOut,
            y: 20,
            opacity: 0,
        });
    }

    export let dropdownClass: string | null = null;
    export let iconClass: string | null = null;
    export let iconUrl: string | null = null;
    export let imageId: number | null = null;
    export let srLabel: string | null = null;
    export let label: string | null = null;
    export let withCaret: boolean = false;
    export let positioning: Partial<FloatingConfig> = {};
    export let countBadge: number | null = null;
    export let onClick: (event: MouseEvent) => void = () => {};

    let type: 'popover' | 'menu' = $$slots.popover != null ? 'popover' : 'menu';

    const popover = createPopover({
        defaultOpen: false,
        preventScroll: false,
        arrowSize: 12,
        positioning: {
            gutter: 0,
            overflowPadding: 0,
            ...positioning,
        },
    });

    const menu = createDropdownMenu({
        defaultOpen: false,
        preventScroll: false,
        arrowSize: 12,
        positioning: {
            gutter: 0,
            overflowPadding: 0,
            ...positioning,
        },
    });

    const trigger = type === 'popover' ? popover.elements.trigger : menu.elements.trigger;
    const open = type === 'popover' ? popover.states.open : menu.states.open;
    const content = type === 'popover' ? popover.elements.content : menu.elements.menu;
    const arrow = type === 'popover' ? popover.elements.arrow : menu.elements.arrow;

    const meltItem = menu.elements.item;

    interface $$Slots {
        popover: Record<string, never>;
        menu: {
            meltMenu: typeof menu;
            meltItem: StoreElement<typeof menu.elements.item>;
        };
    }
</script>

<ButtonNavbar
    meltItem={$trigger}
    open={$open}
    {onClick}
    {srLabel}
    {label}
    {withCaret}
    {iconClass}
    {iconUrl}
    {imageId}
    {countBadge}
/>

{#if $open}
    <section
        use:melt={$content}
        class={cx(
            dropdownClass ?? '',
            'p-0 m-0 overflow-hidden bg-white shadow-dropdown z-navbar-dropdown',
            'rounded-b-[16px] rounded-t-none',
            'w-full ls:w-auto',
            'outline-none',
        )}
        in:transitionIn
        out:transitionOut
    >
        <div use:melt={$arrow} class="bg-brand-navbar-blue-alt" />
        {#if type === 'popover'}
            <slot name="popover" />
        {:else}
            <slot name="menu" meltMenu={menu} meltItem={$meltItem} />
        {/if}
    </section>
{/if}
